/**
 * Pure tree-shape helpers for the manifest/catalog editor (docs/343 v0 §2). No
 * React, no network — the caller (`useEditorTree.ts`/`EditorTree.tsx`) supplies the
 * already-loaded indices/bodies + a resolver (`entryUrls.ts`'s `ownedPackIdFromUrl`/
 * `ownedCatalogIdFromUrl`); this file just answers "what does the tree look like."
 *
 * Deliberately URL-based, NOT `entry.id`-based: a catalog entry's own `id` is a
 * per-placement identifier that need not equal the referenced document's canonical
 * `id` (docs/055-STORED-REFS-CANONICAL-IDS.md's whole point — a cross-listed
 * pack/catalog can carry a different `id` at each placement). The only reliable
 * signal that an entry points at one of THIS admin's own saved documents is its
 * `manifestUrl`/`url` matching the exact gated path this editor itself mints.
 */

export interface CatalogEntryDraft {
  kind: "pack" | "catalog";
  id: string;
  title: string;
  manifestUrl?: string;
  url?: string;
  inline?: unknown;
  [key: string]: unknown;
}

export interface CatalogDraft {
  specVersion?: number;
  id: string;
  title: string;
  entries?: CatalogEntryDraft[];
  [key: string]: unknown;
}

/** Every owned pack id (via `resolveOwnedPackId`) referenced by ANY loaded
 *  catalog's `entries` (kind:"pack"). Feeds "Unassigned packs." */
export function referencedPackIds(
  catalogs: CatalogDraft[],
  resolveOwnedPackId: (url: string | undefined) => string | null,
): Set<string> {
  const ids = new Set<string>();
  for (const c of catalogs) {
    for (const e of c.entries ?? []) {
      if (e.kind !== "pack") continue;
      const owned = resolveOwnedPackId(e.manifestUrl);
      if (owned) ids.add(owned);
    }
  }
  return ids;
}

/** Manifest ids not referenced (by owned URL) by any loaded catalog's entries,
 *  in index order. */
export function unassignedPackIds(
  allPackIds: string[],
  catalogs: CatalogDraft[],
  resolveOwnedPackId: (url: string | undefined) => string | null,
): string[] {
  const referenced = referencedPackIds(catalogs, resolveOwnedPackId);
  return allPackIds.filter((id) => !referenced.has(id));
}

/**
 * Resolve a `catalog`-kind entry's nested children — the referenced catalog's own
 * `entries`, when its `url` resolves (via `resolveOwnedCatalogId`) to one of THIS
 * admin's own saved/loaded catalogs — with a cycle guard (a catalog that
 * (transitively) references itself renders its own entries only once, not
 * infinitely). Returns `null` for an unresolved/external reference (a foreign URL,
 * or an `inline` embed — v1, docs/343 §9).
 */
export function resolveNestedCatalogId(
  entry: CatalogEntryDraft,
  resolveOwnedCatalogId: (url: string | undefined) => string | null,
  knownCatalogIds: ReadonlySet<string>,
  visited: ReadonlySet<string>,
): string | null {
  if (entry.kind !== "catalog") return null;
  const ownedId = resolveOwnedCatalogId(entry.url);
  if (!ownedId || visited.has(ownedId)) return null;
  return knownCatalogIds.has(ownedId) ? ownedId : null;
}
